import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { spacing } from "#app/theme/v2/spacing";
import { IconButton } from "#components/atoms/IconButton/IconButton";
import { Tooltip } from "#components/atoms/Tooltip/Tooltip";
import { Select } from "#components/molecules/Select/Select";
import { createTableColumnHelper } from "#components/organisms/Table/tableHook";
import { Material } from "#modules/materials/infra/fetchCustomMaterials.api";
import { UpdateMaterialDto } from "#modules/materials/infra/updateMaterial.api";
import { useMaterialGradeDetails } from "#modules/materials/view/useMaterialGradeDetails";
import { DimensionsCell } from "#modules/onboardingV2/view/rawMaterialSpecification/DimensionsCell";
import { MaterialNameCell } from "#modules/onboardingV2/view/rawMaterialSpecification/MaterialNameCell";
import { PriceCell } from "#modules/onboardingV2/view/rawMaterialSpecification/PriceCell";
import { Icon } from "#shared/icons/Icon";
import {
  getMaterialDensity,
  MaterialGradeType,
  MaterialTypeSchema,
  MaterialTypeType,
} from "shared";

const columnHelper = createTableColumnHelper<Material>();

export const useRawMaterialSpecificationColumns = (
  onUpdateMaterial: (id: string, payload: UpdateMaterialDto) => void,
  onDeleteMaterial: (material: Material) => void,
) => {
  const { t } = useTranslation();
  const { materialGradeDetails } = useMaterialGradeDetails();

  const formatOptions = useMemo(
    () =>
      MaterialTypeSchema.options.map((format) => ({
        value: format,
        label: t(`qrl.materialTypes.${format}`),
      })),
    [t],
  );

  const materialGradeOptions = useMemo(
    () =>
      materialGradeDetails.map(({ materialGrade, name }) => ({
        value: materialGrade,
        label: name,
      })),
    [materialGradeDetails],
  );

  return useMemo(
    () => [
      columnHelper.accessor("type", {
        id: "format",
        header: () => t("onboardingV2.rawMaterialSpecification.columns.format"),
        size: 216,
        sortFn: (rowA, rowB) =>
          t(`qrl.materialTypes.${rowA.original.type}`).localeCompare(
            t(`qrl.materialTypes.${rowB.original.type}`),
          ),
        cell: ({ row: { original } }) => (
          <Select
            value={original.type}
            onChange={(value) =>
              value && onUpdateMaterial(original.id, { type: value as MaterialTypeType })
            }
            data={formatOptions}
          />
        ),
      }),
      columnHelper.display({
        id: "materialGrade",
        header: () => t("onboardingV2.rawMaterialSpecification.columns.materialGrade"),
        cell: ({ row: { original } }) => (
          <Select
            value={original.materialGrade}
            onChange={(value) => {
              if (!value) {
                return;
              }
              const materialGrade = value as MaterialGradeType;
              onUpdateMaterial(original.id, {
                materialGrade,
                density: getMaterialDensity({ materialGrade }),
              });
            }}
            data={materialGradeOptions}
          />
        ),
      }),
      columnHelper.display({
        id: "materialName",
        header: () => t("onboardingV2.rawMaterialSpecification.columns.materialName"),
        cell: ({ row: { original } }) => (
          <MaterialNameCell
            value={original.name}
            onUpdate={(name) => onUpdateMaterial(original.id, { name })}
          />
        ),
      }),
      columnHelper.display({
        id: "dimensions",
        header: () => t("onboardingV2.rawMaterialSpecification.columns.dimensions"),
        size: 228,
        cell: ({ row: { original } }) => (
          <DimensionsCell
            material={original}
            onUpdate={(payload) => onUpdateMaterial(original.id, payload)}
          />
        ),
      }),
      columnHelper.display({
        id: "price",
        header: () => t("onboardingV2.rawMaterialSpecification.columns.price"),
        size: 228,
        cell: ({ row: { original } }) => (
          <PriceCell
            value={original.price}
            onUpdate={(price) => onUpdateMaterial(original.id, { price })}
          />
        ),
      }),
      columnHelper.display({
        id: "delete",
        header: () => null,
        meta: {
          verticalPadding: spacing[0],
          horizontalPadding: spacing[0],
        },
        size: 32,
        cell: ({ row: { original } }) => (
          <Tooltip label={t("common.delete")}>
            <IconButton
              variant="transparent"
              size="sm"
              aria-label={t("common.delete")}
              onClick={() => onDeleteMaterial(original)}
            >
              <Icon name="trash" size={16} />
            </IconButton>
          </Tooltip>
        ),
      }),
    ],
    [t, formatOptions, materialGradeOptions, onUpdateMaterial, onDeleteMaterial],
  );
};
